import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Check,
  Copy,
  ExternalLink,
  Image as ImageIcon,
  Inbox,
  Loader2,
  RefreshCw,
  Sparkles,
  Wand2,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { getProjects } from '@/lib/api';
import {
  getFormularfrei,
  richteFormularfreiEin,
  schalteLeistung,
  verbindeZustellung,
  type FormularfreiStand,
  type FfLeistung,
} from '@/lib/formularfrei-api';

/**
 * Der Tab „formularfrei" eines Kunden (14.09.2026).
 *
 * Der Besucher der Kundenwebsite lädt ein Foto seines Raums hoch, beantwortet
 * höchstens drei Fragen und bekommt dasselbe Zimmer nach der fertigen Arbeit
 * zu sehen. Bild und Angaben gehen als Anfrage an den Betrieb.
 *
 * Verwaltet wird das hier und nicht auf formularfrei.ai — Nico, 14.09.2026:
 * „Die sollen gar keinen Zugang zu formularfrei.ai bekommen, sondern einfach
 * nur das Feature." Der Betrieb ist ohnehin im LMS, neben Publivo und der
 * Search Console. Ein zweites Portal für vier Schalter wäre ein zweites
 * Passwort, das er sich nicht merkt.
 *
 * Was hier BEWUSST FEHLT, ist eine Liste der Anfragen. Die stehen im Tab
 * „Anfragen", zusammen mit allen anderen — eine zweite Liste erzeugt nur die
 * Frage, welche von beiden die richtige ist.
 */

interface Props {
  /** Der Kunde, dessen Arbeitsbereich gerade offen ist. */
  customerId: string;
}

function zahl(n: number): string {
  return new Intl.NumberFormat('de-DE').format(n);
}

/** „2026-09" → „September 2026" */
function monatName(monat?: string): string {
  if (!monat) return 'diesen Monat';
  const [j, m] = monat.split('-').map(Number);
  if (!j || !m) return 'diesen Monat';
  return new Date(j, m - 1, 1).toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
}

function KopierKnopf({ text, titel }: { text: string; titel: string }) {
  const [fertig, setFertig] = useState(false);
  return (
    <button
      type="button"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setFertig(true);
          setTimeout(() => setFertig(false), 1600);
          toast.success(`${titel} kopiert`);
        } catch {
          toast.error('Kopieren hat nicht geklappt — bitte von Hand markieren.');
        }
      }}
      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-gray-200 text-[12px] font-medium text-gray-600 hover:bg-gray-50"
    >
      {fertig ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
      {fertig ? 'Kopiert' : 'Kopieren'}
    </button>
  );
}

/** Ein Schalter je Zeile — angeboten, ja oder nein. */
function Schalter({ an, laeuft, onClick }: { an: boolean; laeuft: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={an}
      disabled={laeuft}
      onClick={onClick}
      className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition disabled:opacity-50 ${an ? 'bg-primary-600' : 'bg-gray-300'}`}
    >
      <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition ${an ? 'translate-x-[1.15rem]' : 'translate-x-1'}`} />
    </button>
  );
}

export default function FormularfreiPanel({ customerId }: Props) {
  const queryClient = useQueryClient();
  const [projektId, setProjektId] = useState<string>('');

  const { data: projekte, isLoading: projekteLaden } = useQuery({
    queryKey: ['projects', customerId],
    queryFn: () => getProjects(customerId),
    enabled: !!customerId,
  });

  /*
   * Recruiting-Projekte bleiben außen vor: Wer eine Stelle sucht, lädt kein
   * Foto seines Wohnzimmers hoch. Ein Modul dort anzubieten, wäre nur eine
   * Zeile mehr, die nie jemand einschaltet.
   */
  const passende = useMemo(
    () => (projekte || []).filter((p) => p.active && p.project_type !== 'bewerbungen'),
    [projekte]
  );

  useEffect(() => {
    if (!projektId && passende.length) setProjektId(passende[0].id);
  }, [passende, projektId]);

  const schluessel = ['formularfrei', projektId];
  const { data: stand, isLoading, isFetching, refetch } = useQuery({
    queryKey: schluessel,
    queryFn: () => getFormularfrei(projektId),
    enabled: !!projektId,
    staleTime: 60_000,
  });

  const einrichten = useMutation({
    mutationFn: () => richteFormularfreiEin(projektId),
    onSuccess: (neu) => {
      queryClient.setQueryData(schluessel, neu);
      toast.success(neu.eingerichtet ? 'Modul eingerichtet' : 'Gelesen, aber noch nichts eingerichtet');
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : 'Einrichten fehlgeschlagen'),
  });

  const zustellung = useMutation({
    mutationFn: () => verbindeZustellung(projektId),
    onSuccess: (neu) => {
      queryClient.setQueryData(schluessel, neu);
      toast.success('Zustellung verbunden');
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : 'Verbinden fehlgeschlagen'),
  });

  const [inArbeit, setInArbeit] = useState<string | null>(null);
  const leistungSchalten = useMutation({
    mutationFn: (v: { id: string; angeboten: boolean }) => schalteLeistung(projektId, v.id, v.angeboten),
    onMutate: (v) => setInArbeit(v.id),
    onSuccess: (neu) => queryClient.setQueryData(schluessel, neu),
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : 'Schalter nicht gesetzt'),
    onSettled: () => setInArbeit(null),
  });

  if (projekteLaden) {
    return <div className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="w-4 h-4 animate-spin" /> Lade…</div>;
  }
  if (!passende.length) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-4 text-[13px] text-gray-500">
        Für diesen Kunden gibt es kein Projekt, in das sich das Anfragemodul einbauen ließe.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Projektwahl — nur, wenn es etwas zu wählen gibt. */}
      {passende.length > 1 && (
        <div className="flex flex-wrap items-center gap-2">
          <label className="text-[13px] text-gray-500">Projekt</label>
          <select
            value={projektId}
            onChange={(e) => setProjektId(e.target.value)}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-[13px] bg-white"
          >
            {passende.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>
      )}

      {isLoading || !stand ? (
        <div className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="w-4 h-4 animate-spin" /> Lade Stand…</div>
      ) : !stand.konfiguriert ? (
        <div className="bg-white rounded-xl border border-gray-200 p-4 text-[13px] text-gray-500">
          Die Verbindung zu formularfrei.ai ist serverseitig noch nicht eingerichtet
          (Env-Variable <code className="mx-1 px-1 bg-gray-100 rounded">FORMULARFREI_PARTNER_TOKEN</code>).
        </div>
      ) : !stand.erreichbar ? (
        <div className="bg-white rounded-xl border border-amber-200 p-4">
          <p className="text-[13px] text-amber-900">{stand.fehler || 'formularfrei.ai hat nicht geantwortet.'}</p>
          <button
            onClick={() => refetch()}
            className="inline-flex items-center gap-1.5 mt-3 px-3 py-1.5 rounded-lg border border-gray-200 text-[13px] font-medium text-gray-700 hover:bg-gray-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isFetching ? 'animate-spin' : ''}`} /> Noch einmal versuchen
          </button>
        </div>
      ) : !stand.eingerichtet ? (
        <NochNicht stand={stand} laeuft={einrichten.isPending} onEinrichten={() => einrichten.mutate()} />
      ) : (
        <>
          {/*
            Eingerichtet, aber ohne Verbindung ins LMS: Das Modul erzeugt dann
            Bilder (die Geld kosten) und die Anfrage kommt nirgends an. Das darf
            nicht erst auffallen, wenn der Kunde nach seinen Anfragen fragt.
          */}
          {!stand.zustellung && (
            <div className="bg-white rounded-xl border border-amber-200 p-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-[13px] font-medium text-amber-900">Die Anfragen kommen noch nicht im Posteingang an.</p>
                <p className="text-[12px] text-amber-800">Das Modul läuft, aber die Verbindung ins LMS fehlt.</p>
              </div>
              <button
                onClick={() => zustellung.mutate()}
                disabled={zustellung.isPending}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-[13px] font-medium disabled:opacity-50"
              >
                {zustellung.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Inbox className="w-3.5 h-3.5" />}
                Zustellung verbinden
              </button>
            </div>
          )}
          <Einbau stand={stand} />
          <Zahlen stand={stand} customerId={customerId} />
          <Leistungen
            leistungen={stand.leistungen}
            inArbeit={inArbeit}
            onSchalten={(l, an) => leistungSchalten.mutate({ id: l.id, angeboten: an })}
          />
        </>
      )}
    </div>
  );
}

/** Noch nicht eingerichtet: ein Knopf, der die Website liest. */
function NochNicht({ stand, laeuft, onEinrichten }: { stand: FormularfreiStand; laeuft: boolean; onEinrichten: () => void }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="w-4 h-4 text-primary-600" />
        <h2 className="text-[13px] font-semibold text-gray-900">Anfragemodul einrichten</h2>
      </div>
      <p className="text-[13px] text-gray-500 max-w-2xl">
        Das Modul liest die Website des Betriebs und leitet daraus ab, was er anbietet und
        wie seine Seite aussieht. Danach stehen hier seine Leistungen mit einem Schalter je
        Zeile — und die zwei Zeilen zum Einbauen.
      </p>

      {stand.projekt.website ? (
        <p className="mt-3 text-[13px] text-gray-500">
          Gelesen wird: <span className="font-mono text-xs text-gray-800">{stand.projekt.website}</span>
        </p>
      ) : (
        <p className="mt-3 text-[13px] text-amber-800">
          Für dieses Projekt ist keine Website hinterlegt (Search-Console-Property). Ohne die Seite des
          Betriebs gibt es nichts zu lesen — das gehört zuerst in die{' '}
          <Link to="/einrichtung" className="underline">Einrichtung</Link>.
        </p>
      )}

      <button
        onClick={onEinrichten}
        disabled={laeuft || !stand.projekt.website}
        className="inline-flex items-center gap-2 mt-4 px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium disabled:opacity-50"
      >
        {laeuft ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
        {laeuft ? 'Liest die Website…' : 'Website lesen und einrichten'}
      </button>
      {laeuft && <p className="mt-2 text-[12px] text-gray-400">Das dauert bis zu zwei Minuten — die Seite wird komplett gelesen.</p>}
    </div>
  );
}

/** Adresse und die zwei Zeilen zum Einbauen. */
function Einbau({ stand }: { stand: FormularfreiStand }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h2 className="text-[13px] font-semibold text-gray-900">Eigene Adresse</h2>
            {/* Eingerichtet, aber abgeschaltet: Ohne diesen Hinweis stünde die
                Adresse da, als würde sie Anfragen annehmen. */}
            {!stand.aktiv && (
              <span className="text-[11px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">abgeschaltet</span>
            )}
          </div>
          {stand.seite ? (
            <a
              href={stand.seite}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-[13px] text-blue-600 hover:underline break-all"
            >
              {stand.seite.replace(/^https:\/\//, '')} <ExternalLink className="w-3.5 h-3.5 flex-shrink-0" />
            </a>
          ) : (
            <p className="text-[13px] text-gray-400">noch keine Adresse vergeben</p>
          )}
          {/* Die Kennung ist die technische Identität — sie steckt in den
              Bildpfaden und in jedem schon ausgelieferten Snippet. */}
          <p className="mt-0.5 text-[11px] text-gray-400 font-mono">{stand.kennung}</p>
        </div>
        {stand.seite && <KopierKnopf text={stand.seite} titel="Adresse" />}
      </div>

      <div>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
          <div>
            <h2 className="text-[13px] font-semibold text-gray-900">Zum Einbauen</h2>
            <p className="text-[12px] text-gray-500">Diese zwei Zeilen dort einsetzen, wo das Modul auf der Seite stehen soll.</p>
          </div>
          <KopierKnopf text={stand.einbau} titel="Snippet" />
        </div>
        <pre className="p-3 rounded-lg bg-gray-900 text-gray-100 text-[12px] leading-relaxed overflow-x-auto"><code>{stand.einbau}</code></pre>
      </div>
    </div>
  );
}

/** Drei Zahlen — und die eine, die zwischen ihnen steht. */
function Zahlen({ stand, customerId }: { stand: FormularfreiStand; customerId: string }) {
  const z = stand.zahlen;
  const karten = [
    {
      label: 'Bilder',
      wert: z ? zahl(z.bilder_monat) : '—',
      icon: ImageIcon,
      farbe: 'text-violet-600 bg-violet-50',
      hinweis: 'Erzeugte Vorher-Nachher-Bilder in diesem Monat. Jedes kostet Geld.',
    },
    {
      label: 'Anfragen',
      wert: z ? zahl(z.anfragen_monat) : '—',
      icon: Inbox,
      farbe: 'text-green-600 bg-green-50',
      hinweis: 'Anfragen aus dem Modul in diesem Monat. Sie stehen im Posteingang unter „Anfragen" und zählen dort in derselben Kennzahl wie alle anderen.',
    },
    {
      label: 'Bild gesehen, nicht angefragt',
      wert: z ? zahl(z.ohne_anfrage) : '—',
      icon: Sparkles,
      farbe: 'text-amber-600 bg-amber-50',
      hinweis: 'Besucher, die ihr fertiges Bild bekommen und den letzten Schritt trotzdem nicht gemacht haben. Die Zahl, an der sich ablesen lässt, ob das Anfrageformular danach im Weg steht.',
    },
  ];

  return (
    <div>
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
        <h2 className="text-[13px] font-semibold text-gray-900">{monatName(z?.monat)}</h2>
        <Link to={`/kunden/${customerId}?tab=anfragen`} className="text-[12px] text-blue-600 hover:underline">
          Die Anfragen selbst stehen im Posteingang →
        </Link>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {karten.map((k) => (
          <div key={k.label} className="bg-white rounded-xl border border-gray-200 p-4" title={k.hinweis}>
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-medium text-gray-500 uppercase tracking-wider">{k.label}</span>
              <span className={`p-1.5 rounded-lg ${k.farbe}`}><k.icon className="w-4 h-4" /></span>
            </div>
            <p className="mt-2 text-2xl font-bold text-gray-900">{k.wert}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Die Leistungen mit einem Schalter je Zeile. */
function Leistungen({
  leistungen,
  inArbeit,
  onSchalten,
}: {
  leistungen: FfLeistung[];
  inArbeit: string | null;
  onSchalten: (l: FfLeistung, an: boolean) => void;
}) {
  const an = leistungen.filter((l) => l.angeboten).length;
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-1">
        <h2 className="text-[13px] font-semibold text-gray-900">Leistungen</h2>
        <span className="text-[12px] text-gray-400">{an} von {leistungen.length} werden angeboten</span>
      </div>
      {/*
        Warum man etwas ABSCHALTET, obwohl der Betrieb es macht: Nicht jede
        Leistung taugt für ein Bild. Eine getauschte Dichtung sieht man nicht,
        eine Wärmepumpe verkauft sich über Zahlen — und manches verbietet sich
        ganz. Bei einer alteingesessenen Schreinerei stand zwischen Fenstern
        und Treppen „Bestattungen".
      */}
      <p className="text-[12px] text-gray-500 mb-3 max-w-2xl">
        Angeboten wird, wo man den Unterschied auf einem Foto sieht und das Bild etwas weckt.
        Reparaturen, reine Technik und alles Pietätvolle bleiben aus.
      </p>

      {leistungen.length === 0 ? (
        <p className="text-[13px] text-gray-400">Beim Lesen der Website wurde keine Leistung gefunden.</p>
      ) : (
        <ul className="divide-y divide-gray-50">
          {leistungen.map((l) => (
            <li key={l.id} className="py-2.5 flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <p className={`text-[13px] ${l.angeboten ? 'text-gray-900' : 'text-gray-400'}`}>{l.label}</p>
                {l.beschreibung && (
                  <p className="text-[12px] text-gray-400 line-clamp-2">{l.beschreibung}</p>
                )}
              </div>
              {inArbeit === l.id ? (
                <Loader2 className="w-4 h-4 animate-spin text-gray-400 mt-0.5" />
              ) : (
                <Schalter an={l.angeboten} laeuft={!!inArbeit} onClick={() => onSchalten(l, !l.angeboten)} />
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
